/**
 * Texts of screen E6 · Home (specs/M1-home/spec.md §4).
 *
 * Provisional until Q3 (tone; French uses "tu", as in the mockups) and Q15 (EN mockups).
 * Reason texts (TELEGRAM_SESSION_LOST, …) live in the C2 catalogue, not here.
 */
export const homeFr = {
  title: 'Accueil',
  loading: 'Chargement…',
  retry: 'Réessayer',
  emergencyStop: 'Arrêt d’urgence',
  status: {
    operational: 'Copie active',
    incomplete: 'La copie n’est pas encore active',
    incompleteHint: 'Aucun signal n’est copié d’ici là.',
    suspended: 'Copie en pause',
    suspendedHint: 'Tous tes canaux réglés sont en pause.',
    blocked: 'Copie arrêtée depuis {{time}}',
    accountLine: '{{platform}} · {{server}}',
    equity: 'Équité',
    demo: 'Démo',
  },
  missing: {
    title: 'Ce qui manque',
    telegram_login: 'Connecter Telegram',
    choose_channel: 'Choisir au moins un canal',
    connect_trading_account: 'Connecter ton compte MT5',
    configure_channel: 'Régler au moins un canal',
  },
  blocked: {
    reidentify_telegram: 'Me reconnecter à Telegram',
    reconnect_trading_account: 'Reconnecter mon compte',
    positionsRemain_one: '{{count}} position reste ouverte chez ton courtier.',
    positionsRemain_other: '{{count}} positions restent ouvertes chez ton courtier.',
  },
  dailyLoss: {
    title: 'Perte du jour',
    ratio: '{{loss}} / {{limit}}',
    noLimit: 'Aucune limite journalière',
    // M1 R20 (Proposal, to be validated; Q232): no reading while the account is reachable.
    notAvailable: 'Indisponible pour le moment',
    notAvailableHint:
      'Si tu as fixé une limite journalière, les nouvelles ouvertures peuvent être refusées tant que ta perte du jour ne peut pas être lue. Les clôtures continuent.',
    approached: 'Tu as atteint la moitié de ta perte maximale du jour.',
    reached:
      'Perte maximale du jour atteinte : aucune nouvelle ouverture jusqu’à minuit (heure de Paris). Les clôtures continuent.',
  },
  channels: {
    title: 'Mes canaux',
    manage: 'Gérer',
    empty: 'Aucun canal choisi',
    toConfigureHint: 'Ne copie rien tant qu’il n’est pas réglé',
    // P8 R6 (Proposal, to be validated): saved in confirmation while the mode is hidden.
    confirmationUnavailableHint: 'Confirmation indisponible : choisis un autre mode',
    copyState: {
      active: 'Actif',
      paused: 'En pause',
      to_configure: 'À régler',
      suspended_limit: 'Suspendu (limite)',
      suspended_external: 'Suspendu',
    },
    setting: {
      risk_percent: 'Risque {{value}}',
      fixed_lot: 'Lot fixe {{value}}',
      automatic: 'Automatique',
      confirmation: 'Confirmation',
      summary: '{{size}} · {{mode}}',
    },
  },
  positions: {
    title: 'Positions ouvertes · {{total}}',
    seeAll: 'Voir tout',
    empty: 'Aucune position ouverte',
    buy: 'BUY',
    sell: 'SELL',
    lots: '{{lots}} lot',
    /** P10 R6–R8: total risk at stop loss and positions without a stop, above the preview. */
    risk: {
      total: 'Risque total au SL',
      unavailable: 'Indisponible pour le moment',
      withoutStop_one: '{{count}} position sans stop',
      withoutStop_other: '{{count}} positions sans stop',
    },
  },
  suggestion: {
    title: 'Passer en mode automatique ?',
    body: 'Aucune réponse sur {{channel}} depuis 2 semaines. Rien ne change si tu restes en confirmation.',
    action: 'Ouvrir les réglages',
  },
};

/** Shape of the `home` section of `Messages`. */
export type HomeMessages = typeof homeFr;

/** English texts of screen E6. `HomeMessages` makes a missing or extra key a compile error (X5). */
export const homeEn: HomeMessages = {
  title: 'Home',
  loading: 'Loading…',
  retry: 'Retry',
  emergencyStop: 'Emergency stop',
  status: {
    operational: 'Copy active',
    incomplete: 'Copy not active yet',
    incompleteHint: 'No signal is copied until then.',
    suspended: 'Copy paused',
    suspendedHint: 'All your set-up channels are paused.',
    blocked: 'Copy stopped since {{time}}',
    accountLine: '{{platform}} · {{server}}',
    equity: 'Equity',
    demo: 'Demo',
  },
  missing: {
    title: 'What’s missing',
    telegram_login: 'Connect Telegram',
    choose_channel: 'Choose at least one channel',
    connect_trading_account: 'Connect your MT5 account',
    configure_channel: 'Set up at least one channel',
  },
  blocked: {
    reidentify_telegram: 'Reconnect Telegram',
    reconnect_trading_account: 'Reconnect my account',
    positionsRemain_one: '{{count}} open position remains at your broker.',
    positionsRemain_other: '{{count}} open positions remain at your broker.',
  },
  dailyLoss: {
    title: 'Daily loss',
    ratio: '{{loss}} / {{limit}}',
    noLimit: 'No daily limit set',
    // M1 R20 (Proposal, to be validated; Q232): no reading while the account is reachable.
    notAvailable: 'Not available right now',
    notAvailableHint:
      'If you set a daily limit, new openings may be refused until your loss today can be read. Closes still apply.',
    approached: 'You have reached half of your maximum daily loss.',
    reached:
      'Maximum daily loss reached: no new opening until midnight (Paris time). Closes still apply.',
  },
  channels: {
    title: 'My channels',
    manage: 'Manage',
    empty: 'No channel chosen yet',
    toConfigureHint: 'Copies nothing until it is set up',
    confirmationUnavailableHint: 'Confirmation unavailable: choose another mode',
    copyState: {
      active: 'Active',
      paused: 'Paused',
      to_configure: 'To set up',
      suspended_limit: 'Suspended (limit)',
      suspended_external: 'Suspended',
    },
    setting: {
      risk_percent: 'Risk {{value}}',
      fixed_lot: 'Fixed lot {{value}}',
      automatic: 'Automatic',
      confirmation: 'Confirmation',
      summary: '{{size}} · {{mode}}',
    },
  },
  positions: {
    title: 'Open positions · {{total}}',
    seeAll: 'See all',
    empty: 'No open position',
    buy: 'BUY',
    sell: 'SELL',
    lots: '{{lots}} lot',
    risk: {
      total: 'Total risk at SL',
      unavailable: 'Not available right now',
      withoutStop_one: '{{count}} position without a stop',
      withoutStop_other: '{{count}} positions without a stop',
    },
  },
  suggestion: {
    title: 'Switch to automatic mode?',
    body: 'No answer on {{channel}} for 2 weeks. Nothing changes if you stay in confirmation.',
    action: 'Open settings',
  },
};
